import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import {
  ERPPage, ERPPageHeader, ERPKPIGrid, ERPKPICard, ERPActionBar, ERPButton,
  ERPError, ERPTableTh, ERPTableRow, ERPTableTd, ERPBadge, ERPLoading,
  ERPEmpty, ERPPagination, ERPSearchInput, ERPFilterSelect,
} from '../../components/ui/ERPLayout';
import unifiedClientService, {
  getClientType, CLIENT_TYPE_LABELS, CLIENT_TYPE_COLORS,
} from '../../services/unifiedClientService';

const ClientesUnificados = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ tipo: 'all', status: 'all' });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { applyFilters(); }, [clients, search, filters]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await unifiedClientService?.getAll();
      setClients(data || []);
    } catch (err) {
      setError(err?.message || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let list = [...clients];
    if (search) {
      const s = search?.toLowerCase();
      list = list?.filter(c =>
        c?.nombre?.toLowerCase()?.includes(s) ||
        c?.nombreComercial?.toLowerCase()?.includes(s) ||
        c?.nifCif?.toLowerCase()?.includes(s) ||
        c?.contactoPrincipal?.toLowerCase()?.includes(s) ||
        c?.email?.toLowerCase()?.includes(s)
      );
    }
    if (filters?.tipo !== 'all') {
      list = list?.filter(c => getClientType(c) === filters?.tipo);
    }
    if (filters?.status !== 'all') {
      list = list?.filter(c => c?.activo === (filters?.status === 'active'));
    }
    setFiltered(list);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'reset') { setFilters({ tipo: 'all', status: 'all' }); setSearch(''); }
    else setFilters(prev => ({ ...prev, [key]: value }));
  };

  const counts = {
    total: clients?.length,
    salaCorte: clients?.filter(c => c?.clienteSalaCorte)?.length,
    distribucion: clients?.filter(c => c?.clienteDistribucion)?.length,
    ambos: clients?.filter(c => c?.clienteSalaCorte && c?.clienteDistribucion)?.length,
    activos: clients?.filter(c => c?.activo)?.length,
  };

  const kpis = [
    { title: 'Total Clientes', value: counts?.total, subtitle: 'En el sistema', icon: 'Users', iconColor: '#D6EF4E', valueColor: '#D6EF4E' },
    { title: 'Sala de Corte', value: counts?.salaCorte, subtitle: 'Clientes loncheado', icon: 'Scissors', iconColor: '#C9A24D', valueColor: '#C9A24D' },
    { title: 'Distribución', value: counts?.distribucion, subtitle: 'Clientes distribución', icon: 'Truck', iconColor: '#8097D6', valueColor: '#8097D6' },
    { title: 'Ambos', value: counts?.ambos, subtitle: 'Doble actividad', icon: 'GitMerge', iconColor: '#3A7F6B', valueColor: '#3A7F6B' },
    { title: 'Activos', value: counts?.activos, subtitle: 'Clientes activos', icon: 'CheckCircle', iconColor: '#3A7F6B', valueColor: '#3A7F6B' },
  ];

  const totalPages = Math.max(1, Math.ceil(filtered?.length / pageSize));
  const paginated = filtered?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>
      <Helmet><title>Clientes — Las Dos Doncellas</title></Helmet>
      <ERPPage>
        <ERPPageHeader
          title="Clientes"
          subtitle="Modelo único de clientes compartido por todos los módulos"
          actions={
            <ERPButton variant="primary" icon="Plus" onClick={() => navigate('/clientes/nuevo')}>
              Nuevo Cliente
            </ERPButton>
          }
        />

        <ERPKPIGrid cols={5}>
          {kpis?.map(k => <ERPKPICard key={k?.title} {...k} loading={loading} />)}
        </ERPKPIGrid>

        <ERPActionBar>
          <ERPSearchInput
            value={search}
            onChange={(v) => { setSearch(v); setCurrentPage(1); }}
            placeholder="Buscar por nombre, NIF, email..."
          />
          <ERPFilterSelect value={filters?.tipo} onChange={(v) => handleFilterChange('tipo', v)}>
            <option value="all">Todos los tipos</option>
            <option value="SALA_CORTE">Sala de Corte</option>
            <option value="DISTRIBUCION">Distribución</option>
            <option value="AMBOS">Ambos</option>
            <option value="NINGUNO">Sin clasificar</option>
          </ERPFilterSelect>
          <ERPFilterSelect value={filters?.status} onChange={(v) => handleFilterChange('status', v)}>
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </ERPFilterSelect>
          <ERPButton variant="ghost" icon="X" onClick={() => handleFilterChange('reset')} size="sm">
            Limpiar
          </ERPButton>
        </ERPActionBar>

        {error && <div className="mb-4"><ERPError message={error} onRetry={loadClients} /></div>}

        <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-oranienbaum">
              <thead>
                <tr className="border-b border-white/8 bg-[#141414]">
                  <ERPTableTh sortable={false} onClick={() => {}}>#</ERPTableTh>
                  <ERPTableTh sortable={false} onClick={() => {}}>Cliente</ERPTableTh>
                  <ERPTableTh sortable={false} onClick={() => {}}>NIF / CIF</ERPTableTh>
                  <ERPTableTh sortable={false} onClick={() => {}}>Tipo</ERPTableTh>
                  <ERPTableTh sortable={false} onClick={() => {}}>Contacto</ERPTableTh>
                  <ERPTableTh sortable={false} onClick={() => {}}>Estado</ERPTableTh>
                  <ERPTableTh sortable={false} onClick={() => {}}>Acciones</ERPTableTh>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr><td colSpan={7} className="py-16"><ERPLoading /></td></tr>
                ) : !paginated?.length ? (
                  <tr><td colSpan={7} className="py-16"><ERPEmpty message="No se encontraron clientes" /></td></tr>
                ) : (
                  paginated?.map((client, idx) => {
                    const globalIdx = (currentPage - 1) * pageSize + idx + 1;
                    const tipo = getClientType(client);
                    const tipoColor = CLIENT_TYPE_COLORS?.[tipo];
                    const tipoLabel = CLIENT_TYPE_LABELS?.[tipo];
                    return (
                      <ERPTableRow
                        key={client?.id}
                        selected={false}
                        onClick={() => navigate(`/clientes/${client?.id}`)}
                      >
                        <ERPTableTd>
                          <span className="text-white/40 text-xs">{globalIdx}</span>
                        </ERPTableTd>
                        <ERPTableTd>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#D6EF4E]/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-[#D6EF4E]">
                                {client?.nombre?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-white/90 leading-tight">{client?.nombre}</p>
                              {client?.nombreComercial && client?.nombreComercial !== client?.nombre && (
                                <p className="text-xs text-white/40">{client?.nombreComercial}</p>
                              )}
                            </div>
                          </div>
                        </ERPTableTd>
                        <ERPTableTd>
                          <span className="text-white/55 text-xs">{client?.nifCif || '—'}</span>
                        </ERPTableTd>
                        <ERPTableTd>
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: `${tipoColor}18`, color: tipoColor, border: `1px solid ${tipoColor}30` }}
                          >
                            {tipoLabel}
                          </span>
                        </ERPTableTd>
                        <ERPTableTd>
                          <div className="text-white/55 text-xs">
                            <p>{client?.contactoPrincipal || client?.telefono || '—'}</p>
                            {client?.email && <p className="text-white/35">{client?.email}</p>}
                          </div>
                        </ERPTableTd>
                        <ERPTableTd>
                          <ERPBadge color={client?.activo ? 'green' : 'red'}>
                            {client?.activo ? 'Activo' : 'Inactivo'}
                          </ERPBadge>
                        </ERPTableTd>
                        <ERPTableTd>
                          <div className="flex items-center gap-1" onClick={(e) => e?.stopPropagation()}>
                            <button
                              onClick={() => navigate(`/clientes/${client?.id}`)}
                              className="p-1.5 rounded-lg text-white/40 hover:text-[#D6EF4E] hover:bg-[#D6EF4E]/8 transition-colors"
                              title="Ver ficha"
                            >
                              <Icon name="Eye" size={13} />
                            </button>
                            <button
                              onClick={() => navigate(`/clientes/${client?.id}`)}
                              className="p-1.5 rounded-lg text-white/40 hover:text-[#8097D6] hover:bg-[#8097D6]/8 transition-colors"
                              title="Editar"
                            >
                              <Icon name="Edit" size={13} />
                            </button>
                          </div>
                        </ERPTableTd>
                      </ERPTableRow>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="border-t border-white/8 px-4 py-3">
              <ERPPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={filtered?.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={() => {}}
              />
            </div>
          )}
        </div>
      </ERPPage>
    </>
  );
};

export default ClientesUnificados;
